/* eslint-disable react-hooks/exhaustive-deps */
import { TextareaAutosize } from '@mui/material';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getSearch, update } from '../../services/item';
import { deepCloneArray, findIndexOfObjectInArray } from '../../utils/array';
import { ModalGlobal } from '../modal/modal.Global';
import getCaretCoordinates from 'textarea-caret';
import { getId } from '../../utils/item';
import { renderIconBySlug, searchIcons } from '../../content/icons';
import { DangerZone } from '../form/settings.Widget';
import { PicSelectionOptions } from '../pic/pic.Selector';
import { TextMd } from './text.Md';
import {
  FaBold,
  FaItalic,
  FaStrikethrough,
  FaHeading,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaEyeSlash,
  FaListUl,
  FaListOl,
  FaQuoteRight,
  FaCode,
  FaLink,
  FaImage,
  FaMinus,
  FaAt,
} from 'react-icons/fa';

export const TextSettings = props => {
  const { itemId, textId, text } = props;

  const item = useSelector(state => state.items?.[itemId]);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const isMobile = useSelector(state => state.global.isMobile);
  const isModalOpen = useSelector(
    state => state.global.openModales[textId + itemId],
  );
  const textareaRef = useRef(null);
  const wasModalOpenRef = useRef(false);
  const selectionRef = useRef({ start: 0, end: 0 });

  const [value, setValue] = useState('');
  const [isMention, setIsMention] = useState(null);
  const [mentionCaret, setMentionCaret] = useState(null);
  const [searchedItemsList, setSearchedItemsList] = useState([]);
  const [iconTrigger, setIconTrigger] = useState(null);
  const [iconCaret, setIconCaret] = useState(null);
  const [filteredIcons, setFilteredIcons] = useState([]);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [imageMenuOpen, setImageMenuOpen] = useState(false);
  const [imageCaret, setImageCaret] = useState(null);
  const [showWysiwygToolbar, setShowWysiwygToolbar] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('textSettings.showWysiwyg');
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch (e) {
      // ignore
    }
    return true;
  });
  const [showPreview, setShowPreview] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('textSettings.showPreview');
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch (e) {
      // ignore
    }
    return true;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        'textSettings.showWysiwyg',
        showWysiwygToolbar ? '1' : '0',
      );
    } catch (e) {
      // ignore
    }
  }, [showWysiwygToolbar]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        'textSettings.showPreview',
        showPreview ? '1' : '0',
      );
    } catch (e) {
      // ignore
    }
  }, [showPreview]);

  const colorTokens = [
    { token: 'p', label: 'Primaire', swatchClass: 'bg-primary' },
    { token: 's', label: 'Secondaire', swatchClass: 'bg-secondary' },
    { token: 'red', label: 'Rouge', swatchClass: 'bg-red' },
    { token: 'orange', label: 'Orange', swatchClass: 'bg-orange' },
    { token: 'yellow', label: 'Jaune', swatchClass: 'bg-yellow' },
    { token: 'lime', label: 'Lime', swatchClass: 'bg-lime' },
    { token: 'green', label: 'Vert', swatchClass: 'bg-green' },
    { token: 'blue', label: 'Bleu', swatchClass: 'bg-blue' },
    { token: 'purple', label: 'Violet', swatchClass: 'bg-purple' },
    { token: 'pink', label: 'Rose', swatchClass: 'bg-pink' },
  ];

  const applyTextareaEdit = editorFn => {
    const el = textareaRef.current;
    if (!el || typeof editorFn !== 'function') {
      return;
    }
    const start =
      typeof el.selectionStart === 'number'
        ? el.selectionStart
        : selectionRef.current.start || 0;
    const end =
      typeof el.selectionEnd === 'number'
        ? el.selectionEnd
        : selectionRef.current.end || start;
    const currentValue = typeof el.value === 'string' ? el.value : value || '';
    const result = editorFn({
      value: currentValue,
      start,
      end,
      before: currentValue.slice(0, start),
      selected: currentValue.slice(start, end),
      after: currentValue.slice(end),
    });

    if (!result) {
      return;
    }
    const replaceStart =
      typeof result.replaceStart === 'number' ? result.replaceStart : start;
    const replaceEnd =
      typeof result.replaceEnd === 'number' ? result.replaceEnd : end;
    const replacement =
      typeof result.replacement === 'string' ? result.replacement : null;
    const nextValue =
      typeof result.nextValue === 'string'
        ? result.nextValue
        : replacement !== null
          ? `${currentValue.slice(0, replaceStart)}${replacement}${currentValue.slice(replaceEnd)}`
          : null;

    if (nextValue === null) {
      return;
    }

    setValue(nextValue);

    if (
      typeof result.nextSelectionStart === 'number' &&
      typeof result.nextSelectionEnd === 'number'
    ) {
      const applySelection = () => {
        const nextEl = textareaRef.current;
        if (!nextEl) {
          return;
        }
        nextEl.focus();
        nextEl.setSelectionRange(
          result.nextSelectionStart,
          result.nextSelectionEnd,
        );
        selectionRef.current = {
          start: result.nextSelectionStart,
          end: result.nextSelectionEnd,
        };
      };
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(applySelection);
      } else {
        applySelection();
      }
    }
  };

  const wrapSelection = (prefix, suffix, placeholder = '') => {
    applyTextareaEdit(({ before, selected, after, start, end, value }) => {
      const hasSelection = selected.length > 0;
      const content = hasSelection ? selected : placeholder;
      const replacement = `${prefix}${content}${suffix}`;
      const nextValue = `${before}${replacement}${after}`;
      const selStart = start + prefix.length;
      const selEnd = hasSelection
        ? end + prefix.length
        : selStart + content.length;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement,
        nextSelectionStart: selStart,
        nextSelectionEnd: selEnd,
      };
    });
  };

  const insertAtCursor = (
    insertion,
    { selectFrom, selectTo, caretOffset } = {},
  ) => {
    applyTextareaEdit(({ before, selected, after, start }) => {
      const nextValue = `${before}${insertion}${after}`;
      const base = start;
      const nextSelectionStart =
        typeof selectFrom === 'number'
          ? base + selectFrom
          : base + (caretOffset ?? insertion.length);
      const nextSelectionEnd =
        typeof selectTo === 'number' ? base + selectTo : nextSelectionStart;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: start,
        replacement: insertion,
        nextSelectionStart,
        nextSelectionEnd,
      };
    });
  };

  const prefixSelectedLines = prefix => {
    applyTextareaEdit(({ value, start, end }) => {
      const safe = typeof value === 'string' ? value : '';
      const lineStart = safe.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
      const lineEndRaw = safe.indexOf('\n', end);
      const lineEnd = lineEndRaw === -1 ? safe.length : lineEndRaw;
      const block = safe.slice(lineStart, lineEnd);
      const lines = block.split('\n');
      const nextBlock = lines.map(l => (l ? `${prefix}${l}` : l)).join('\n');
      const nextValue =
        safe.slice(0, lineStart) + nextBlock + safe.slice(lineEnd);
      const delta = nextBlock.length - block.length;
      return {
        nextValue,
        replaceStart: lineStart,
        replaceEnd: lineEnd,
        replacement: nextBlock,
        nextSelectionStart: start + (start === lineStart ? prefix.length : 0),
        nextSelectionEnd: end + delta,
      };
    });
  };

  const insertLink = () => {
    applyTextareaEdit(({ before, selected, after, start, end }) => {
      const label = selected || 'texte';
      const insertion = `[${label}](url)`;
      const nextValue = `${before}${insertion}${after}`;
      const urlStart = start + insertion.lastIndexOf('(url)') + 1;
      const urlEnd = urlStart + 3;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement: insertion,
        nextSelectionStart: urlStart,
        nextSelectionEnd: urlEnd,
      };
    });
  };

  const insertCollapse = () => {
    applyTextareaEdit(({ value, before, selected, after, start, end }) => {
      const title = 'Section';
      const content = selected && selected.trim() ? selected : 'Contenu';

      const needsLeadingNl = before.length > 0 && !before.endsWith('\n');
      const needsTrailingNl = after.length > 0 && !after.startsWith('\n');
      const prefix = needsLeadingNl ? '\n' : '';
      const suffix = needsTrailingNl ? '\n' : '';

      const insertion = `${prefix}::: collapse ${title}\n${content}\n:::${suffix}`;
      const nextValue = `${before}${insertion}${after}`;

      const titleStart = start + prefix.length + '::: collapse '.length;
      const titleEnd = titleStart + title.length;

      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement: insertion,
        nextSelectionStart: titleStart,
        nextSelectionEnd: titleEnd,
      };
    });
  };

  const insertImage = () => {
    applyTextareaEdit(({ before, selected, after, start }) => {
      const alt = selected || 'alt';
      const insertion = `![${alt}](url)`;
      const nextValue = `${before}${insertion}${after}`;
      const urlStart = start + insertion.lastIndexOf('(url)') + 1;
      const urlEnd = urlStart + 3;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: start,
        replacement: insertion,
        nextSelectionStart: urlStart,
        nextSelectionEnd: urlEnd,
      };
    });
  };

  const imageOptions = useMemo(() => {
    if (!Array.isArray(item?.pics)) {
      return [];
    }
    const options = [];
    for (let i = 0; i < item.pics.length; i++) {
      const picObj = item.pics[i];
      const picId = getId(picObj);
      const payload = picId ? picObj?.[picId] : null;
      const url = payload?.url || '';
      if (!picId || !url) {
        continue;
      }
      options.push({
        key: picId,
        label: `Image ${options.length + 1}`,
        previewUrl: url,
      });
    }
    return options;
  }, [item?.pics]);

  const applyPickedImage = picId => {
    const found = imageOptions.find(opt => opt.key === picId);
    const url = found?.previewUrl;
    if (!url) {
      return;
    }
    setImageMenuOpen(false);
    applyTextareaEdit(({ before, selected, after, start }) => {
      const alt = selected || 'image';
      const insertion = `![${alt}](${url})`;
      const nextValue = `${before}${insertion}${after}`;
      const nextCaret = start + insertion.length;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: start,
        replacement: insertion,
        nextSelectionStart: nextCaret,
        nextSelectionEnd: nextCaret,
      };
    });
  };

  const ToolbarBtn = ({ title, onClick, children }) => {
    return (
      <button
        type='button'
        title={title}
        className='px-05 py-03 rounded bg-main bg-opacity-40 hover:bg-opacity-60 border border-primary border-opacity-15 text-contrast text-sm inline-flex items-center gap-05'
        onMouseDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={onClick}
      >
        {children}
      </button>
    );
  };

  const applyHighlightColor = token => {
    setColorMenuOpen(false);
    const safeToken = typeof token === 'string' ? token.trim() : '';
    if (!safeToken) return;

    applyTextareaEdit(({ before, selected, after, start, end }) => {
      const hasSelection = selected.length > 0;
      const content = hasSelection ? selected : 'texte';
      const lines = content.split('\n');
      const nextContent = lines
        .map(line => (line ? `==${line}=={${safeToken}}` : line))
        .join('\n');
      const nextValue = `${before}${nextContent}${after}`;

      // Select the original content (or placeholder) inside the first `==...=={...}`
      const firstLine = lines[0] || 'texte';
      const innerStart = start + 2;
      const innerEnd = start + 2 + firstLine.length;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement: nextContent,
        nextSelectionStart: hasSelection ? start : innerStart,
        nextSelectionEnd: hasSelection ? start + nextContent.length : innerEnd,
      };
    });
  };

  const searchIn = {
    type: false,
    universe: true,
    world: true,
    campaign: false,
  };

  const updateText = nextValue => {
    if (!Array.isArray(item?.text)) {
      return;
    }
    const resolvedValue =
      typeof nextValue === 'string'
        ? nextValue
        : typeof textareaRef.current?.value === 'string'
          ? textareaRef.current.value
          : value;
    const clonedTexts = deepCloneArray(item.text);
    const idx = findIndexOfObjectInArray(clonedTexts, textId);
    if (idx === -1) {
      return;
    }
    const currentStored = clonedTexts[idx]?.[textId]?.text ?? '';
    if (currentStored === resolvedValue) {
      return;
    }
    clonedTexts[idx][textId].text = resolvedValue;
    update(`items/${itemId}`, 'text', clonedTexts);
  };

  const deleteText = () => {
    const clonedText = deepCloneArray(item.text);
    const idx = clonedText.findIndex(textObj => getId(textObj) === textId);
    if (idx > -1) {
      clonedText.splice(idx, 1);
      update(`items/${itemId}`, 'text', clonedText);
    }
  };

  const applyCurrentMention = item => {
    const mention = isMention?.mention;
    const cursor = isMention?.cursor;
    if (!mention || typeof cursor !== 'number') {
      return;
    }
    const insertion = `[${item.label}](${item._id})`;
    applyTextareaEdit(({ value }) => {
      const safe = typeof value === 'string' ? value : '';
      const start = cursor;
      const end = cursor + mention.length;
      const nextValue = safe.slice(0, start) + insertion + safe.slice(end);
      const nextCaret = start + insertion.length;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement: insertion,
        nextSelectionStart: nextCaret,
        nextSelectionEnd: nextCaret,
      };
    });
    setIsMention(null);
  };

  const getCurrentIconTrigger = () => {
    if (!value || !textareaRef.current) {
      return null;
    }
    const idxCursor = textareaRef.current.selectionStart;
    const slicedValue = value.slice(0, idxCursor);
    const regex = /:icon\.?[a-zA-Z0-9_-]*$/i;
    const match = regex.exec(slicedValue);
    if (match) {
      const matchedText = match[0];
      const startIndex = match.index ?? slicedValue.length - matchedText.length;
      const charBefore =
        startIndex > 0 ? slicedValue.charAt(startIndex - 1) : '';
      if (charBefore && /[a-zA-Z0-9]/.test(charBefore)) {
        return null;
      }
      const query = matchedText.replace(/:icon\.?/i, '').toLowerCase();
      const startCursor = idxCursor - matchedText.length;
      return {
        token: matchedText,
        query,
        cursor: startCursor,
      };
    }
    return null;
  };

  const applyCurrentIcon = icon => {
    if (!icon || !iconTrigger || !icon.slug) {
      return;
    }
    const insertion = `:icon.${icon.slug}: `;
    const cursor = iconTrigger?.cursor;
    const token = iconTrigger?.token;
    if (typeof cursor !== 'number' || !token) {
      return;
    }
    applyTextareaEdit(({ value }) => {
      const safe = typeof value === 'string' ? value : '';
      const start = cursor;
      const end = cursor + token.length;
      const nextValue = safe.slice(0, start) + insertion + safe.slice(end);
      const caretPos = start + insertion.length;
      return {
        nextValue,
        replaceStart: start,
        replaceEnd: end,
        replacement: insertion,
        nextSelectionStart: caretPos,
        nextSelectionEnd: caretPos,
      };
    });
    setIconTrigger(null);
    setFilteredIcons([]);
  };

  const getCurrentMention = () => {
    const regex = /@([a-zA-Z0-9_.-]*)/g;
    if (value) {
      const idxCursor = textareaRef.current.selectionStart;
      const slicedValue = value.slice(0, idxCursor);
      const matches = slicedValue.match(regex);
      if (matches) {
        const lastMention = matches[matches.length - 1];
        const isEditingLastMention =
          lastMention ===
          value.slice(idxCursor - lastMention.length, idxCursor);

        return {
          mention: isEditingLastMention ? lastMention : null,
          cursor: idxCursor - lastMention.length,
        };
      }
    }
    return null;
  };

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }
    const currentMention = getCurrentMention();
    setIsMention(currentMention);
    setMentionCaret(
      currentMention
        ? getCaretCoordinates(textareaRef.current, currentMention.cursor)
        : null,
    );

    const currentIcon = getCurrentIconTrigger();
    setIconTrigger(currentIcon);
    setIconCaret(
      currentIcon
        ? getCaretCoordinates(textareaRef.current, currentIcon.cursor)
        : null,
    );
  }, [textareaRef, value]);

  useEffect(() => {
    if (isMention?.mention) {
      const search = async () => {
        const res = await getSearch(
          isMention?.mention,
          item,
          searchIn,
          listCampaignsId,
        );
        if (Array.isArray(res)) {
          setSearchedItemsList(res);
        } else {
          setSearchedItemsList([]);
        }
      };
      search();
    }
  }, [isMention]);

  useEffect(() => {
    if (iconTrigger) {
      const query = iconTrigger.query || '';
      const results = searchIcons(query);
      setFilteredIcons(results.slice(0, 8));
    } else {
      setFilteredIcons([]);
    }
  }, [iconTrigger]);

  useEffect(() => {
    if (isModalOpen === true) {
      const next = text?.text ?? '';
      setValue(next);
    }
  }, [isModalOpen]);

  useEffect(() => {
    const wasOpen = wasModalOpenRef.current;
    if (wasOpen && !isModalOpen) {
      updateText(value);
    }
    wasModalOpenRef.current = Boolean(isModalOpen);
  }, [isModalOpen, value]);

  return (
    <ModalGlobal
      title={'Edition du texte de ' + item?.label}
      idToOpen={textId + itemId}
      width={isMobile ? undefined : showPreview ? 980 : 720}
      maxWidthClass={isMobile ? undefined : 'max-w-none'}
    >
      <div className='w-full'>
        <div className='relative flex flex-wrap items-center  gap-03 mb-05'>
          {!isMobile && (
            <ToolbarBtn
              title={showPreview ? "Masquer l'aperçu" : "Afficher l'aperçu"}
              onClick={() => setShowPreview(prev => !prev)}
            >
              {showPreview ? <FaEyeSlash /> : <FaEye />}
              <span className='text-xs opacity-80'>aperçu</span>
            </ToolbarBtn>
          )}

          <ToolbarBtn
            title={
              showWysiwygToolbar
                ? 'Masquer les boutons'
                : 'Afficher les boutons'
            }
            onClick={() => {
              setShowWysiwygToolbar(prev => !prev);
              setColorMenuOpen(false);
              setImageMenuOpen(false);
            }}
          >
            {showWysiwygToolbar ? <FaChevronUp /> : <FaChevronDown />}
            <span className='text-xs opacity-80'>
              {showWysiwygToolbar ? 'réduire' : 'outils'}
            </span>
          </ToolbarBtn>

          {showWysiwygToolbar && (
            <>
              <ToolbarBtn
                title='Gras'
                onClick={() => wrapSelection('**', '**', 'texte')}
              >
                <FaBold />
              </ToolbarBtn>
              <ToolbarBtn
                title='Italique'
                onClick={() => wrapSelection('*', '*', 'texte')}
              >
                <FaItalic />
              </ToolbarBtn>
              <ToolbarBtn
                title='Barré'
                onClick={() => wrapSelection('~~', '~~', 'texte')}
              >
                <FaStrikethrough />
              </ToolbarBtn>
              <ToolbarBtn
                title='Titre'
                onClick={() => prefixSelectedLines('# ')}
              >
                <FaHeading />
              </ToolbarBtn>
              <ToolbarBtn
                title='Liste'
                onClick={() => prefixSelectedLines('- ')}
              >
                <FaListUl />
              </ToolbarBtn>
              <ToolbarBtn
                title='Liste numérotée'
                onClick={() => prefixSelectedLines('1. ')}
              >
                <FaListOl />
              </ToolbarBtn>
              <ToolbarBtn
                title='Citation'
                onClick={() => prefixSelectedLines('> ')}
              >
                <FaQuoteRight />
              </ToolbarBtn>
              <ToolbarBtn
                title='Section repliable'
                onClick={() => insertCollapse()}
              >
                <span className='text-xs font-mono'>collapse</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Code inline'
                onClick={() => wrapSelection('`', '`', 'code')}
              >
                <FaCode />
              </ToolbarBtn>
              <ToolbarBtn
                title='Bloc de code'
                onClick={() => insertAtCursor('```\n\n```', { caretOffset: 4 })}
              >
                <FaCode />
                <span className='text-xs opacity-80'>bloc</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Lien'
                onClick={() => insertLink()}
              >
                <FaLink />
              </ToolbarBtn>
              <ToolbarBtn
                title='Image'
                onClick={() => {
                  const el = textareaRef.current;
                  if (!el) {
                    insertImage();
                    return;
                  }
                  const caret = getCaretCoordinates(el, el.selectionStart ?? 0);
                  setImageCaret(caret);
                  setImageMenuOpen(open => !open);
                }}
              >
                <FaImage />
              </ToolbarBtn>
              <ToolbarBtn
                title='Séparateur'
                onClick={() => insertAtCursor('\n---\n', { caretOffset: 5 })}
              >
                <FaMinus />
              </ToolbarBtn>

              <ToolbarBtn
                title='Couleur (surlignage)'
                onClick={() => setColorMenuOpen(open => !open)}
              >
                <span className='text-xs font-mono'>couleur</span>
              </ToolbarBtn>
              {colorMenuOpen && (
                <div
                  className='absolute z-50 top-full left-0 mt-05 rounded shadow-lg bg-main border border-primary border-opacity-20 p-05 flex flex-wrap gap-05 max-w-420'
                  onMouseDown={e => e.preventDefault()}
                >
                  {colorTokens.map(c => (
                    <button
                      key={c.token}
                      type='button'
                      title={c.label}
                      className='px-05 py-03 rounded bg-main bg-opacity-30 hover:bg-opacity-60 border border-primary border-opacity-10 text-sm text-contrast inline-flex items-center gap-05'
                      onClick={() => applyHighlightColor(c.token)}
                    >
                      <span
                        className={`inline-block rounded ${c.swatchClass}`}
                        style={{ width: 12, height: 12, opacity: 0.9 }}
                      />
                      <span className='text-xs opacity-80'>{c.label}</span>
                    </button>
                  ))}
                </div>
              )}

              <ToolbarBtn
                title='Insérer un lien vers un item (@)'
                onClick={() => insertAtCursor('@', { caretOffset: 1 })}
              >
                <FaAt />
                <span className='text-xs opacity-80'>item</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Insérer une icône (:icon...)'
                onClick={() => insertAtCursor(':icon.', { caretOffset: 6 })}
              >
                <span className='text-xs font-mono'>:icon:</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Insérer un select'
                onClick={() =>
                  insertAtCursor('{{select:Oui|Oui|Non}}', {
                    selectFrom: '{{select:'.length,
                    selectTo: '{{select:Oui|Oui|Non}}'.length - 2,
                  })
                }
              >
                <span className='text-xs font-mono'>select</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Insérer un calcul'
                onClick={() => insertAtCursor('{{= }}', { caretOffset: 4 })}
              >
                <span className='text-xs font-mono'>{'{{='}</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Insérer un ternaire'
                onClick={() =>
                  insertAtCursor('{{condition? vrai : faux}}', {
                    selectFrom: 2,
                    selectTo: 11,
                  })
                }
              >
                <span className='text-xs font-mono'>?:</span>
              </ToolbarBtn>
              <ToolbarBtn
                title='Insérer une référence de num'
                onClick={() =>
                  insertAtCursor('[current.numLabel]', {
                    selectFrom: '[current.'.length,
                    selectTo: '[current.numLabel]'.length - 1,
                  })
                }
              >
                <span className='text-xs font-mono'>num</span>
              </ToolbarBtn>
            </>
          )}
        </div>

        <div
          className={
            isMobile || !showPreview
              ? 'w-full mt-1 mb-1'
              : 'w-full flex gap-1 mt-1 mb-1'
          }
        >
          <div className={isMobile ? 'w-full' : 'flex-1 min-w-0'}>
            <div className='relative w-full'>
              <TextareaAutosize
                ref={textareaRef}
                aria-label='empty textarea'
                placeholder='Empty'
                minRows={3}
                className='bg-transparent text-contrast block'
                style={{ width: '100%' }}
                value={value}
                onChange={e => setValue(e.target.value)}
                onSelect={e => {
                  selectionRef.current = {
                    start: e.currentTarget.selectionStart ?? 0,
                    end: e.currentTarget.selectionEnd ?? 0,
                  };
                }}
                onKeyUp={e => {
                  selectionRef.current = {
                    start: e.currentTarget.selectionStart ?? 0,
                    end: e.currentTarget.selectionEnd ?? 0,
                  };
                }}
                onClick={e => {
                  selectionRef.current = {
                    start: e.currentTarget.selectionStart ?? 0,
                    end: e.currentTarget.selectionEnd ?? 0,
                  };
                }}
                onBlur={e => {
                  selectionRef.current = {
                    start: e.currentTarget.selectionStart ?? 0,
                    end: e.currentTarget.selectionEnd ?? 0,
                  };
                }}
              />

              <div
                className={`${
                  imageMenuOpen ? '' : 'hidden'
                } absolute z-50 bg-primary-d shadow text-contrast rounded max-h-260 overflow-auto`}
                style={{
                  top: imageCaret?.top ? imageCaret.top + imageCaret.height : 0,
                  left: imageCaret?.left ?? 0,
                  minWidth: 260,
                }}
                onMouseDown={e => e.preventDefault()}
              >
                {imageOptions.length === 0 ? (
                  <div className='px-1 py-05 text-sm opacity-70'>
                    Aucune image sur cet item
                  </div>
                ) : (
                  <PicSelectionOptions
                    title={null}
                    options={imageOptions}
                    value={null}
                    onChange={applyPickedImage}
                    size={34}
                    maxHeight={240}
                    className='p-05'
                  />
                )}
              </div>

              <div
                className={`${
                  isMention?.mention ? '' : 'hidden'
                } absolute z-50 bg-primary-d shadow text-contrast rounded max-h-240 overflow-auto`}
                style={{
                  top: mentionCaret?.top
                    ? mentionCaret.top + mentionCaret.height
                    : 0,
                  left: mentionCaret?.left ?? 0,
                }}
                onMouseDown={e => e.preventDefault()}
              >
                {searchedItemsList.map(item => (
                  <div
                    className='px-1 py-05 cursor-pointer hover:bg-primary hover:bg-opacity-20'
                    key={item._id}
                    onClick={() => applyCurrentMention(item)}
                  >
                    {item.label}
                  </div>
                ))}
              </div>

              <div
                className={`${
                  iconTrigger ? '' : 'hidden'
                } absolute z-50 bg-primary-d shadow text-contrast rounded max-h-240 overflow-auto`}
                style={{
                  top: iconCaret?.top ? iconCaret.top + iconCaret.height : 0,
                  left: iconCaret?.left ?? 0,
                  minWidth: 180,
                }}
                onMouseDown={e => e.preventDefault()}
              >
                {filteredIcons.length === 0 && (
                  <div className='px-1 py-05 text-sm opacity-70'>
                    Aucun icône trouvé
                  </div>
                )}
                {filteredIcons.map(icon => (
                  <div
                    key={icon.slug || icon.label}
                    className='px-1 py-05 cursor-pointer hover:bg-primary hover:bg-opacity-20 flex items-center gap-05'
                    onClick={() => applyCurrentIcon(icon)}
                  >
                    <span className='inline-flex'>
                      {renderIconBySlug(icon.slug, { size: 20 })}
                    </span>
                    <span>{icon.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {!isMobile && showPreview && (
            <div className='flex-1 min-w-0 rounded border border-primary border-opacity-30 bg-primary-d bg-opacity-20 p-05'>
              <div className=' p-05 overflow-auto'>
                <div style={{ pointerEvents: 'none' }}>
                  <TextMd
                    str={value}
                    textId={`text-preview-${textId}-${itemId}`}
                    itemId={itemId}
                    style={{ width: '100%', color: 'inherit' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DangerZone
        confirmId={`deleteText-${textId}-${itemId}`}
        closeModalId={textId + itemId}
        message='Voulez-vous vraiment supprimer ce texte ?'
        onConfirm={() => deleteText()}
      />
    </ModalGlobal>
  );
};
